import { Typography, Box, Checkbox } from '@mui/material'
import { type GridRenderCellParams, type GridColDef } from '@mui/x-data-grid'
import { colorPrimitives } from 'components/Theme'
import { colors } from 'components/ThemeProvider'
import { type SweepstakeWinnerStatusType } from 'types/api'
import { getStatusStyleColors } from './LocationWinnersColumns'
import { toUpper } from 'lodash'
import { statusTextMap } from 'components/List/FilterStatusList'
import { GrandPrizeMenu } from './GrandPrizeMenu'
import { formatFullDate } from 'utils/util'
import { type RequestRedrawModalDataType } from 'stores/useRequestRedrawModal'

interface GetGrandPrizeWinnersColumnsArgs {
  setIsIndividualRedrawModalOpen: (
    data: RequestRedrawModalDataType | undefined
  ) => void
  contestId: number
  sweepstakeName: string
}

export const getGrandPrizeWinnersColumns = ({
  setIsIndividualRedrawModalOpen,
  contestId,
  sweepstakeName,
}: GetGrandPrizeWinnersColumnsArgs): GridColDef[] => [
  {
    field: 'actions',
    minWidth: 50,
    headerName: '',
    renderCell: (params) => {
      return (
        <GrandPrizeMenu
          params={params}
          contestId={contestId}
          sweepstakeName={sweepstakeName}
          setIsIndividualRedrawModalOpen={() => {
            setIsIndividualRedrawModalOpen({
              winnerName: params.row.winnerName,
              sweepstakeDrawingId: params.row.sweepstakeDrawingId,
              tierName: params.row.tierName,
              sweepstakeWinnerId: params.row.id,
              notes: params.row.notes,
              modifiedOn: params.row.modifiedOn,
            })
          }}
        />
      )
    },
  },
  {
    field: 'tierName',
    minWidth: 200,
    headerName: 'Tier Level',
    flex: 1,
    renderCell: (params: GridRenderCellParams) => (
      <Typography color={colors.text.primary}>{params.value}</Typography>
    ),
  },
  {
    field: 'winnerName',
    minWidth: 200,
    headerName: 'Winner Name',
    flex: 1,
    renderCell: (params: GridRenderCellParams) => (
      <Typography color={colors.text.primary}>{params.value}</Typography>
    ),
  },
  {
    field: 'isMultiWinner',
    minWidth: 180,
    headerName: 'Multi Winner',
    flex: 1,
    sortable: false,
    filterable: false,
    renderCell: (params: GridRenderCellParams) => {
      if (!params.row.isMultiWinner) {
        return null
      }

      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'left',
            width: '100%',
          }}
        >
          <Checkbox
            checked
            disabled
            sx={{
              padding: 0,
              color: colorPrimitives.redGaming,
              '&.Mui-checked': {
                color: colorPrimitives.redGaming,
              },
              '&.Mui-disabled': {
                color: colorPrimitives.redGaming,
              },
            }}
          />
        </Box>
      )
    },
  },
  {
    field: 'prize',
    minWidth: 150,
    headerName: 'Prize',
    flex: 1,
    renderCell: (params: GridRenderCellParams) => (
      <Typography color={colors.text.primary}>{params.value}</Typography>
    ),
  },
  {
    field: 'status',
    headerName: 'Winner Status',
    minWidth: 150,
    flex: 1,
    filterable: false,
    valueGetter: (params) => {
      return params.row.winnerStatus
    },
    renderCell: (params: GridRenderCellParams) => {
      const statusText = params.value as SweepstakeWinnerStatusType
      const style = getStatusStyleColors(
        params.value as SweepstakeWinnerStatusType
      )

      return <Box sx={{ ...style }}>{toUpper(statusTextMap[statusText])}</Box>
    },
  },
  {
    field: 'drawnDate',
    minWidth: 200,
    headerName: 'Date Drawn',
    flex: 1,
    renderCell: (params) => (
      <Typography color={colors.text.primary}>
        {params.value === '-' ? params.value : formatFullDate(params.value)}
      </Typography>
    ),
  },
  {
    field: 'modifiedOn',
    minWidth: 200,
    headerName: 'Status Updated',
    flex: 1,
    renderCell: (params) => (
      <Typography color={colors.text.primary}>
        {formatFullDate(params.row.modifiedOn)}
      </Typography>
    ),
  },
  {
    field: 'drawingSequence',
    hideable: true,
  },
]
